<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import CreateOrderQuickDialog from './CreateOrderQuickDialog.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import { resolveProductsOrdersFeatures } from 'dashboard/helper/productsOrdersFeatures';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const DROPDOWN_SYNC_EVENT = 'chatmigo:conversation-actions-dropdown:open';
const DROPDOWN_SOURCE = 'more';
const resolveActionRef = ref(null);
const quickCreateOrderDialogRef = ref(null);

const currentChat = computed(() => store.getters.getSelectedChat);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const currentAccount = computed(() =>
  store.getters['accounts/getAccount'](currentAccountId.value)
);
const currentInbox = computed(() =>
  store.getters['inboxes/getInbox'](currentChat.value?.inbox_id)
);
const productsOrdersFeatures = computed(() =>
  resolveProductsOrdersFeatures(currentAccount.value?.settings)
);
const isContactSidebarOpen = computed(
  () => uiSettings.value.is_contact_sidebar_open
);
const isWhatsAppConversation = computed(
  () => currentInbox.value?.channel_type === 'Channel::Whatsapp'
);
const isPending = computed(
  () => currentChat.value?.status === wootConstants.STATUS_TYPE.PENDING
);

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-package-plus',
    label: t('PRODUCTS_ORDERS.BUTTONS.CREATE_FROM_CONVERSATION'),
    action: 'create_order',
    value: 'create_order',
  });

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  return items;
});

const closeDropdown = () => toggleDropdown(false);

const toggleMoreActionsDropdown = () => {
  if (!showActionsDropdown.value && typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(DROPDOWN_SYNC_EVENT, {
        detail: { source: DROPDOWN_SOURCE },
      })
    );
  }
  toggleDropdown();
};

const handleExternalDropdownOpen = event => {
  if (event?.detail?.source !== DROPDOWN_SOURCE) {
    closeDropdown();
  }
};

const handleActionClick = ({ action }) => {
  closeDropdown();

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'create_order') {
    if (productsOrdersFeatures.value.quickCreateFromConversation) {
      quickCreateOrderDialogRef.value?.open?.();
    } else {
      router.push({
        name: 'products_orders_index',
        params: { accountId: currentAccountId.value },
        query: {
          openOrder: 'conversation',
          conversationId: currentChat.value.id,
        },
      });
    }
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

const handleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: !isContactSidebarOpen.value,
    is_copilot_panel_open: false,
  });
};

const openTransferShortcut = () => {
  resolveActionRef.value?.openTransferModal?.();
};

const openRescheduleShortcut = () => {
  resolveActionRef.value?.openRescheduleModal?.();
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener(DROPDOWN_SYNC_EVENT, handleExternalDropdownOpen);
  }
});

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
  if (typeof window !== 'undefined') {
    window.removeEventListener(DROPDOWN_SYNC_EVENT, handleExternalDropdownOpen);
  }
});
</script>

<template>
  <div class="relative z-30 flex items-center gap-2 actions--container">
    <ButtonV4
      v-if="!isPending"
      v-tooltip="$t('CONVERSATION.RESOLVE_DROPDOWN.TRANSFER')"
      size="sm"
      variant="ghost"
      color="slate"
      icon="i-lucide-arrow-right-left"
      class="rounded-md hover:bg-n-alpha-2"
      @click="openTransferShortcut"
    />
    <ButtonV4
      v-if="isWhatsAppConversation && !isPending"
      v-tooltip="$t('CONVERSATION.RESOLVE_DROPDOWN.RESCHEDULE')"
      size="sm"
      variant="ghost"
      color="slate"
      icon="i-lucide-calendar-clock"
      class="rounded-md hover:bg-n-alpha-2"
      @click="openRescheduleShortcut"
    />
    <ButtonV4
      v-tooltip="$t('CONVERSATION.SIDEBAR.CONTACT')"
      size="sm"
      variant="ghost"
      color="slate"
      icon="i-lucide-contact"
      class="rounded-md hover:bg-n-alpha-2"
      :class="{ 'bg-n-alpha-2': isContactSidebarOpen }"
      @click="handleConversationSidebarToggle"
    />
    <ResolveAction
      ref="resolveActionRef"
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="closeDropdown"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleMoreActionsDropdown"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="chatmigo-conversation-actions-menu mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <CreateOrderQuickDialog
      ref="quickCreateOrderDialogRef"
      :current-chat="currentChat"
    />
  </div>
</template>
